import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Trees, Heart } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import SanctuaryResidentCard from "@/components/SanctuaryResidentCard";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const FILTER_BUCKETS = [
  { label: "All", match: () => true },
  { label: "Dogs", match: (t) => t === "Dog" },
  { label: "Cats", match: (t) => t === "Cat" },
  { label: "Cows", match: (t) => t === "Cow" },
  { label: "Others", match: (t) => !["Dog", "Cat", "Cow"].includes(t) },
];

export default function PublicSanctuary() {
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/public/sanctuary-residents").then((res) => {
      setResidents(res.data);
      setLoading(false);
    });
  }, []);

  const availableFilters = useMemo(
    () => FILTER_BUCKETS.filter((b) => b.label === "All" || residents.some((r) => b.match(r.animal_type))),
    [residents]
  );

  const filtered = useMemo(() => {
    const bucket = FILTER_BUCKETS.find((b) => b.label === filter) || FILTER_BUCKETS[0];
    return residents.filter((r) => bucket.match(r.animal_type));
  }, [residents, filter]);

  return (
    <div className="min-h-screen bg-amtm-bg">
      <PublicHeader />
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="text-center mb-10">
          <Trees className="h-10 w-10 text-amtm-teal mx-auto mb-4" />
          <h1 className="font-display text-4xl sm:text-5xl text-amtm-text mb-4" data-testid="sanctuary-page-title">Our Sanctuary Family</h1>
          <p className="text-amtm-textMuted max-w-2xl mx-auto">
            Spread across 10+ acres in the hills of Kolad, our cage-free sanctuary is home to 500+ animals who can never return to the streets —
            seniors, the blind and disabled, trauma survivors. They live out their lives here with dignity. You can help feed and care for them.
          </p>
        </div>

        {availableFilters.length > 1 && (
          <div className="flex justify-center gap-2 flex-wrap mb-10" data-testid="sanctuary-filter-chips">
            {availableFilters.map((b) => (
              <button
                key={b.label}
                onClick={() => setFilter(b.label)}
                data-testid={`sanctuary-filter-${b.label.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  filter === b.label ? "bg-amtm-teal text-white" : "bg-amtm-surface border border-amtm-border text-amtm-textMuted"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16" data-testid="sanctuary-loading">
            <Loader2 className="h-8 w-8 animate-spin text-amtm-teal" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-amtm-textMuted py-16" data-testid="sanctuary-empty">
            No sanctuary residents to show yet. Please check back soon.
          </p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sanctuary-grid">
            {filtered.map((c) => (
              <SanctuaryResidentCard key={c.id} caseItem={c} />
            ))}
          </div>
        )}
      </section>

      <div className="bg-amtm-teal text-white py-14 px-6 text-center" data-testid="sanctuary-cta-band">
        <p className="font-display text-2xl md:text-3xl mb-3">Every resident is fed, treated, and loved daily.</p>
        <p className="text-white/85 max-w-xl mx-auto mb-6">Sponsorships directly fund their food and medical care.</p>
        <Button asChild size="lg" className="rounded-full bg-white text-amtm-teal hover:bg-white/90" data-testid="sanctuary-general-donate-button">
          <Link to="/cases">
            <Heart className="h-5 w-5 mr-2" /> Donate
          </Link>
        </Button>
      </div>

      <PublicFooter />
    </div>
  );
}
